// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { streamsForYear } from '../data';
import { STREAMS, FATES, isRecovery } from '../domain';
import { stackedBarHtml, stackedAreaSvg, lineChartSvg, legendHtml, type Seg, type AreaBand } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, formatMt, fyShort } from '../utils/format';

export function renderStreams(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const y = d.latestYear;
  const latest = streamsForYear(d, y);
  const byKey = new Map(latest.map((s) => [s.key, s]));

  const cards = STREAMS.map((s) => {
    const agg = byKey.get(s.key);
    const segs: Seg[] = FATES.map((f) => ({ value: agg?.byFate.get(f.key) || 0, colour: f.colour, label: f.label, tip: `${f.label}: ${formatTonnes(agg?.byFate.get(f.key) || 0)}` }));
    return `<div class="panel" style="margin-bottom:0">
      <div class="panel-title" style="display:flex;align-items:center;gap:8px"><span class="legend-swatch" style="background:${s.colour};width:14px;height:14px"></span>${escapeHtml(s.label)}</div>
      <div class="panel-sub">${escapeHtml(s.note)}</div>
      <div class="stat-grid" style="margin-bottom:var(--space-md)">
        <div class="stat" style="box-shadow:none"><div class="stat-label">Waste</div><div class="stat-value" style="font-size:1.3rem">${formatTonnes(agg?.total || 0)}</div></div>
        <div class="stat" style="box-shadow:none"><div class="stat-label">Recovered</div><div class="stat-value ${(agg?.rate || 0) >= 60 ? 'green' : 'amber'}" style="font-size:1.3rem">${formatPercent(agg?.rate || 0, 0)}</div></div>
      </div>
      ${stackedBarHtml(segs, { tall: true })}
    </div>`;
  }).join('');

  // over-time series
  const genBands: AreaBand[] = STREAMS.map((s) => ({
    key: s.key, label: s.short, colour: s.colour,
    values: d.years.map((yr) => d.streamRows.filter((r) => r.yr === yr && r.s === s.key).reduce((a, r) => a + r.t, 0)),
  }));
  const rateLines = STREAMS.map((s) => ({
    label: s.short, colour: s.colour,
    points: d.years.map((yr, i) => {
      const rows = d.streamRows.filter((r) => r.yr === yr && r.s === s.key);
      const t = rows.reduce((a, r) => a + r.t, 0);
      const rec = rows.reduce((a, r) => a + (isRecovery(r.f) ? r.t : 0), 0);
      return { x: i, y: t > 0 ? (rec / t) * 100 : null };
    }),
    width: 2.4,
  }));

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Household, business &amp; construction</h1>
      <p class="view-sub">Waste is reported in three ${glossaryTerm('stream', 'streams')}: the ${glossaryTerm('msw', 'household')} kerbside bin,
        ${glossaryTerm('ci', 'commercial &amp; industrial')} waste from business, and ${glossaryTerm('cd', 'construction &amp; demolition')}. The bin you fill at home
        is the smallest stream — and not the best recovered.</p>
    </div>
    <div class="grid-2" style="grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr))">${cards}</div>
    <div class="panel">
      <div class="panel-title">Where the tonnage is</div>
      <div class="panel-sub">Waste generated by each stream over time. Construction and commercial waste together dwarf the household bin.</div>
      ${stackedAreaSvg(genBands, d.years.map(fyShort), { width: 760, height: 320, yFmt: (v) => `${formatMt(v)}Mt` })}
      ${legendHtml(STREAMS.map((s) => ({ colour: s.colour, label: s.short })))}
    </div>
    <div class="panel">
      <div class="panel-title">Recovery rate by stream</div>
      <div class="panel-sub">Construction waste — heavy concrete and metal — is recovered best; the household bin lags.</div>
      ${lineChartSvg(rateLines, { width: 760, height: 300, xLabels: d.years.map(fyShort), yMax: 100, yFmt: (v) => `${v.toFixed(0)}%`, markers: true })}
      ${legendHtml(STREAMS.map((s) => ({ colour: s.colour, label: s.short })))}
    </div>
  `;
}
